import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Button, Col} from "reactstrap";
import {useNavigate, useSearchParams} from 'react-router-dom'
import CoverAuth from "./CoverAuth";

import '../../../i18/config';
import {useTranslation} from "react-i18next";
import CoverLangSwitch from "../../../components/CoverLangSwitch";
import ButtonSpinner from "../../../components/ButtonSpinner";
import * as route from "../../../api/routes";
import {getLoggedinUser} from "../../../api/apiCore";
import {
    decideOAuthAuthorizeRequest,
    getOAuthAuthorizeRequest,
    OAuthAuthorizeRequestType
} from "../../../api";
import {forgetOAuthReturn, rememberOAuthReturn} from "../../../helpers/oauthReturn";
import {useRedux} from "../../../hooks";
import {logoutUser} from "../../../redux/actions";

/**
 * OAuth consent: an MCP client (claude.ai, ChatGPT, …) sent the user to
 * m9s-id's /oauth/authorize, which parked the request and redirected here with
 * ?request=areq_…. We make sure the user is signed in (any method), show what
 * the client is asking for, and post the decision; m9s-id answers with the URL
 * to send the browser back to the client.
 */
const CoverOAuthConsent = () => {

    const {t} = useTranslation(['auth']);
    const navigate = useNavigate();
    const {dispatch} = useRedux();
    const [searchParams] = useSearchParams();
    const requestId = searchParams.get('request') || '';
    const returnPath = route.MAIN_OAUTH_CONSENT + '?request=' + encodeURIComponent(requestId);

    const [authRequest, setAuthRequest] = useState<OAuthAuthorizeRequestType | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (requestId.length < 1) {
            setError(t('auth:oauthRequestInvalid'));
            setIsLoading(false);
            return;
        }

        // Remembered even when a session looks present: a stale token makes
        // the API client fail its refresh and hard-redirect to the login page,
        // which must still bring the user back here.
        rememberOAuthReturn(returnPath);

        if (!getLoggedinUser()) {
            navigate(route.MAIN_AUTH_LOGIN);
            return;
        }

        getOAuthAuthorizeRequest(requestId)
            .then((response: any) => {
                if (response?.success === true && response?.item) {
                    forgetOAuthReturn();
                    setAuthRequest(response.item);
                } else {
                    setError(t('auth:oauthRequestInvalid'));
                }
            })
            .catch(() => setError(t('auth:oauthRequestInvalid')))
            .finally(() => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestId]);

    const decide = useCallback((approve: boolean) => {
        setIsSending(true);
        rememberOAuthReturn(returnPath);
        decideOAuthAuthorizeRequest(requestId, approve)
            .then((response: any) => {
                forgetOAuthReturn();
                if (response?.success === true && typeof response?.redirectUrl === 'string') {
                    // Leaves the app: the client finishes the flow on its side.
                    window.location.href = response.redirectUrl;
                    return;
                }
                setError(t('auth:oauthRequestInvalid'));
                setIsSending(false);
            })
            .catch(() => {
                // Kept on failure: a 401 rejects here while the API client is
                // already redirecting to the login page.
                setError(t('auth:oauthRequestInvalid'));
                setIsSending(false);
            });
    }, [requestId, returnPath, t]);

    // Signed into the wrong account (or into someone else's, via a crafted
    // login link): sign out and come back to this same request.
    const switchAccount = useCallback(() => {
        rememberOAuthReturn(returnPath);
        dispatch(logoutUser());
        navigate(route.MAIN_AUTH_LOGIN);
    }, [dispatch, navigate, returnPath]);

    return (
        <CoverAuth title={'Connect an app | Marketcells.io - digital marketing platform'}>
            <Col lg={6}>
                <CoverLangSwitch/>
                <div className="p-lg-5 p-4">
                    {isLoading &&
                        <div className="text-center text-muted py-5"><ButtonSpinner/></div>
                    }

                    {!isLoading && error !== null &&
                        <>
                            <h5 className="text-primary">{t('auth:oauthConnectTitle')}</h5>
                            <Alert color="danger" className="mt-3">{error}</Alert>
                            <Button color="light" className="w-100" onClick={() => navigate(route.MAIN_CABINET_LIST)}>
                                {t('auth:oauthBackToApp')}
                            </Button>
                        </>
                    }

                    {!isLoading && error === null && authRequest !== null &&
                        <>
                            <h5 className="text-primary">{t('auth:oauthConnectTitle')}</h5>
                            <p className="text-muted">
                                {t('auth:oauthWantsAccess', {client: authRequest.client.name})}
                            </p>

                            {!authRequest.client.verified &&
                                <Alert color="warning" className="fs-13">
                                    <strong>{t('auth:oauthUnverifiedTitle')}</strong>{' '}
                                    {t('auth:oauthUnverifiedBody')}
                                </Alert>
                            }

                            {authRequest.account &&
                                <p className="fs-13 mb-2">
                                    {t('auth:oauthSignedInAs', {account: authRequest.account})}{' '}
                                    <Button color="link" size="sm" className="p-0 align-baseline" onClick={switchAccount}>
                                        {t('auth:oauthSwitchAccount')}
                                    </Button>
                                </p>
                            }

                            <ul className="list-unstyled mt-3">
                                {authRequest.scopes.map(scope => (
                                    <li key={scope.scope} className="d-flex mb-2">
                                        <i className="ri-checkbox-circle-line text-success me-2 fs-16"/>
                                        <span>{t('auth:oauthScope_' + scope.scope.replace(':', '_'), {defaultValue: scope.description})}</span>
                                    </li>
                                ))}
                            </ul>

                            <p className="text-muted fs-12 mt-3 mb-1">{t('auth:oauthRolesNote')}</p>
                            {authRequest.redirectUri &&
                                <p className="text-muted fs-12 text-break">
                                    {t('auth:oauthRedirectNote', {host: authRequest.redirectHost ?? ''})}{' '}
                                    <code>{authRequest.redirectUri}</code>
                                </p>
                            }

                            <div className="d-flex gap-2 mt-4">
                                <Button color="light" className="w-50" disabled={isSending} onClick={() => decide(false)}>
                                    {t('auth:oauthDeny')}
                                </Button>
                                <Button color="success" className="w-50" disabled={isSending} onClick={() => decide(true)}>
                                    {t('auth:oauthAllow')}
                                    {isSending && <ButtonSpinner/>}
                                </Button>
                            </div>
                        </>
                    }
                </div>
            </Col>
        </CoverAuth>
    );
};

export default CoverOAuthConsent;
